import { trackEvent } from '@patrianna/analytics'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { CheckCircleOutline } from '@patrianna-icons/material-icons'
import useKYCDocumentVerificationStep from '@patrianna-payments/shared-store/verifications/hooks/docs-verification/useKYCDocumentVerificationStep'
import { setKYCInfoRequest } from '@patrianna-payments/shared-store/verifications/store/actions'
import cx from 'classnames'
import { format, isPast, parseISO } from 'date-fns'

import Button from 'components/Button'
import { stepTwoBtnText } from 'dialogs/RedeemFlow/SubmitDocuments/utils'
import { useReturnAfterVerification } from 'hooks/useReturnAfterVerification'
import content from 'prismic-translates/dialog-identity-verification.json'
import { serviceDependencies } from 'src/services'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { getKycStatusSelector } from 'store/modules/kyc/selectors'
import { getUserIdSelector } from 'store/modules/user/selectors'

import classes from './styles.module.scss'
import SuccessfullyVerified from './SuccessfullyVerified'
import VerificationInProgress from './VerificationInProgress'

import type { VerificationFromProps } from 'hooks/useReturnAfterVerification'

const t = getTranslationFromJson([content])

type Props = {
  isRedeeming: boolean
  verificationFrom: VerificationFromProps
}

export default function DocumentVerificationStep({ isRedeeming, verificationFrom }: Props) {
  const dispatch = useAppDispatch()
  const kycStatus = useAppSelector(getKycStatusSelector)
  const userId = useAppSelector(getUserIdSelector)

  const { error, allowNextUpload, isLoading } = useKYCDocumentVerificationStep(
    { fraud: serviceDependencies.fraud },
    kycStatus,
  )
  const { handleSetVerificationFromToParams } = useReturnAfterVerification()

  const uploadDisabledByDate = allowNextUpload !== null && !isPast(parseISO(allowNextUpload))
  const secondStep = ['id_confirmed', 'doc_declined'].includes(kycStatus)
  const inReview = kycStatus === 'doc_review'
  const disabledBtn = isLoading || isRedeeming || !secondStep || inReview || uploadDisabledByDate

  const secondStepBtnText = stepTwoBtnText(kycStatus)
  const isVerified = secondStepBtnText === 'verified'

  const submitPOA = () => {
    handleSetVerificationFromToParams(verificationFrom)
    void trackEvent('POA_Upload_Initiated', { category: userId })
    dispatch(setKYCInfoRequest({ phase: 'document_verification', error }))
  }

  if (inReview) {
    return <VerificationInProgress title={t('step2_proof_of_address')} text={t('address_verification_in_progress')} />
  }

  if (isVerified) {
    return (
      <SuccessfullyVerified
        dataTest='verifications-verified-address'
        title={t('step2_proof_of_address')}
        text={t('address_verified')}
      />
    )
  }

  return (
    <div className={classes.root}>
      <h4 className={classes.title}>
        <b>{t('step2_proof_of_address')}</b>
      </h4>
      {error && (
        <p className={cx('text-smaller', classes.error)}>
          <img className={classes.iconProcess} src={`${process.env.IMG_ORIGIN}/icons/error.svg`} alt='error-icon' />
          {error}
        </p>
      )}
      <p className={classes.text}>{t('identity_documents')}</p>
      <ul className={classes.list}>
        <li className={classes.listItem}>{t('text_with_check1')}</li>
        <li className={classes.listItem}>{t('text_with_check2')}</li>
        <li className={classes.listItem}>{t('text_with_check3')}</li>
      </ul>

      <Button
        data-test={`verifications-${secondStepBtnText}`}
        disabled={disabledBtn}
        onClick={submitPOA}
        className={classes.button}
      >
        {isVerified && <CheckCircleOutline className={classes.verifiedIcon} />}
        {secondStepBtnText}
      </Button>

      {uploadDisabledByDate && kycStatus === 'doc_declined' && (
        <span className={classes.error}>
          {t('next_upload')} {format(new Date(allowNextUpload), "hh:mm '(PT)' dd MMM yy")}
        </span>
      )}
    </div>
  )
}
